/**
 * Quote List Alpine.js Store & Component
 * Manages quote list state, localStorage persistence, and form submission.
 */

// Utility: Sanitize string to prevent XSS
function sanitizeString(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/[<>]/g, '').substring(0, 500);
}

// Utility: Validate quote item structure
function validateQuoteItem(item) {
  return (
    typeof item === 'object' &&
    item !== null &&
    (typeof item.id === 'number' || typeof item.id === 'string') &&
    typeof item.title === 'string' &&
    typeof item.url === 'string'
  );
}

// Utility: Debounce function
function debounce(fn, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

// Global Alpine Store for quote list - shared between drawer and header
document.addEventListener('alpine:init', () => {
  Alpine.store('quoteList', {
    items: [],
    
    init() {
      this.loadFromStorage();
    },
    
    get hasItems() {
      return this.items.length > 0;
    },
    
    get count() {
      return this.items.length;
    },
    
    loadFromStorage() {
      try {
        const stored = localStorage.getItem('quoteList');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Validate each item from storage
          this.items = Array.isArray(parsed)
            ? parsed.filter(validateQuoteItem)
            : [];
        }
      } catch (e) {
        console.error('Error loading quote list from storage:', e);
        this.items = [];
      }
    },
    
    saveToStorage() {
      try {
        localStorage.setItem('quoteList', JSON.stringify(this.items));
      } catch (e) {
        console.error('Error saving quote list to storage:', e);
      }
    },
    
    add(product) {
      if (!product || !product.id) return false;
      
      const exists = this.items.some(item => item.id === product.id);
      if (!exists) {
        // Sanitize input before storing
        this.items.push({
          id: product.id,
          title: sanitizeString(product.title),
          url: sanitizeString(product.url),
          image: sanitizeString(product.image || '')
        });
        this.saveToStorage();
        return true;
      }
      return false;
    },
    
    remove(id) {
      this.items = this.items.filter(item => item.id !== id);
      this.saveToStorage();
    },
    
    clear() {
      this.items = [];
      this.saveToStorage();
    }
  });
});

// Quote List Drawer Component
function quoteListDrawer() {
  return {
    open: false,
    showForm: false,
    formData: {
      name: '',
      email: '',
      phone: '',
      company: ''
    },
    submitted: false,
    notification: null,
    notificationTimeout: null,
    debouncedSaveFormData: null,

    get items() {
      return this.$store.quoteList.items;
    },

    init() {
      this.loadFormDataFromStorage();
      this.checkFormSubmission();
      
      // Create debounced version of saveFormDataToStorage
      this.debouncedSaveFormData = debounce(() => {
        this.saveFormDataToStorage();
      }, 500);
      
      this.$watch('open', (value) => {
        if (value) {
          this.$store.disableScroll.add('quote-drawer');
          // Focus first focusable element for accessibility
          this.$nextTick(() => {
            this.$el.querySelector('button')?.focus();
          });
        } else {
          this.$store.disableScroll.remove('quote-drawer');
          // Clear notification timeout on close
          if (this.notificationTimeout) {
            clearTimeout(this.notificationTimeout);
            this.notificationTimeout = null;
          }
          if (this.submitted) {
            this.submitted = false;
            this.showForm = false;
          }
        }
      });

      this.$watch('formData', () => {
        this.debouncedSaveFormData();
      }, { deep: true });
    },

    checkFormSubmission() {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('contact_posted')) {
        // Primary conversion. Fired here (not on submit click) because this
        // branch only runs after Shopify has accepted the form and redirected
        // back. Read the items before clear() empties the list.
        const submittedItems = this.$store.quoteList.items;
        window.gtmTrack?.('quote_request_submitted', {
          productCount: submittedItems.length,
          productNames: submittedItems.map((i) => i.title).join(', ')
        });

        this.$store.quoteList.clear();
        this.submitted = true;
        this.showForm = true;
        this.open = true;
        
        const url = new URL(window.location.href);
        url.searchParams.delete('contact_posted');
        url.hash = '';
        window.history.replaceState({}, '', url.toString());
      }
    },

    toggle() {
      if (this.open) {
        return this.close();
      }
      this.open = true;
    },

    close() {
      this.open = false;
    },

    addProduct(product) {
      const added = this.$store.quoteList.add(product);
      if (!added) {
        this.showNotification('exists');
        return;
      }
      window.gtmTrack?.('add_to_quote_list', {
        productId: product.id,
        productName: product.title,
        quoteListSize: this.$store.quoteList.count
      });
      this.open = true;
    },

    removeProduct(id) {
      const item = this.$store.quoteList.items.find((i) => i.id === id);
      window.gtmTrack?.('remove_from_quote_list', {
        productId: id,
        productName: item ? item.title : ''
      });
      this.$store.quoteList.remove(id);
    },

    loadFormDataFromStorage() {
      try {
        const stored = localStorage.getItem('quoteFormData');
        if (stored) {
          const parsed = JSON.parse(stored);
          this.formData = { ...this.formData, ...parsed };
        }
      } catch (e) {
        console.error('Error loading form data from storage:', e);
      }
    },

    saveFormDataToStorage() {
      try {
        localStorage.setItem('quoteFormData', JSON.stringify(this.formData));
      } catch (e) {
        console.error('Error saving form data to storage:', e);
      }
    },

    showQuoteForm() {
      window.gtmTrack?.('quote_form_start', {
        productCount: this.$store.quoteList.count
      });
      this.showForm = true;
    },

    backToList() {
      this.showForm = false;
    },

    get itemCount() {
      return this.$store.quoteList.count;
    },

    get canSubmit() {
      return this.formData.name.trim() !== '' &&
             this.validateEmail(this.formData.email) &&
             this.validatePhone(this.formData.phone) &&
             this.$store.quoteList.items.length > 0;
    },

    validateEmail(email) {
      if (!email || typeof email !== 'string') return false;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email.trim());
    },

    validatePhone(phone) {
      if (!phone || typeof phone !== 'string') return false;
      // Basic: at least 7 digits
      return phone.replace(/\D/g, '').length >= 7;
    },

    showNotification(type) {
      // Clear existing timeout
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }
      this.notification = type;
      this.notificationTimeout = setTimeout(() => {
        this.notification = null;
        this.notificationTimeout = null;
      }, 3000);
    },

    clearList() {
      this.$store.quoteList.clear();
    },

    resetForm() {
      this.formData = {
        name: '',
        email: '',
        phone: '',
        company: ''
      };
    }
  };
}

window.quoteListDrawer = quoteListDrawer;
